import deepmerge from 'deepmerge';
import lf from 'localforage';

/**
 * Long Term Memory main class.
 *
 * @typeparam S - Vuex state type.
 *
 * @public
 */
class LTM {
    execute;
    filter;
    merge;
    reduce;
    storage;
    /**
     * Resolves when the state has been restored or no persisted state was found.
     */
    ready;
    resolveReady = () => { };
    rejectReady = () => { };
    /**
     * @param execute - [[Executor]] that controls when and if the state will be persisted (e.g. to prevent bursts).
     * @param filter - [[Filter]] that decides whether a mutation will trigger persisting.
     * @param merge - [[Merger]] that merges loaded state into Vuex.
     * @param reduce - [[Reducer]] that picks which parts of the state will be persisted.
     * @param storage - [[AsyncStorage]] that saves state data to the store.
     */
    constructor({ execute, filter, merge, reduce, storage, }) {
        this.execute = execute;
        this.filter = filter;
        this.merge = merge;
        this.reduce = reduce;
        this.storage = storage;
        this.ready = new Promise((resolve, reject) => {
            this.resolveReady = resolve;
            this.rejectReady = reject;
        });
    }
    async save(mutation, state) {
        if (!(await this.filter(mutation))) {
            return;
        }
        const partialState = await this.reduce(state);
        if (!partialState) {
            return;
        }
        this.execute(() => {
            this.storage.save(partialState);
        });
    }
    async load(store) {
        const loaded = await this.storage.load();
        if (!loaded) {
            return this.resolveReady();
        }
        const merged = await this.merge(loaded, store.state);
        store.replaceState(merged);
        this.resolveReady();
    }
    /**
     * Vuex plugin (i.e. new Vuex.Store({ plugins: [ltm.plugin]})).
     */
    get plugin() {
        return (store) => {
            store.subscribe((mutation, state) => {
                this.save(mutation, state);
            });
            this.load(store);
        };
    }
}

/**
 * Persists the state immediatelly.
 *
 * @public
 */
const simplyExecute = (f) => {
    f();
};
/**
 * Configures an [[Executor]] that delays state persisting some time after the last change.
 *
 * @param ms - Time to wait before persisting in milliseconds.
 * @returns Configured [[Executor]]
 *
 * @public
 */
function executeWithDelay(ms) {
    let timeout = null;
    function onBeforeunload(e) {
        e.preventDefault(); // Cancel the event
        e.returnValue = ''; // Chrome requires returnValue to be set
    }
    return function executer(func) {
        if (timeout == null) {
            // Starting a new timeout
            window.addEventListener('beforeunload', onBeforeunload);
        }
        else {
            // Replacing existing timeout
            window.clearTimeout(timeout);
        }
        timeout = window.setTimeout(() => {
            func();
            timeout = null;
            window.removeEventListener('beforeunload', onBeforeunload);
        }, ms);
    };
}

/**
 * Persists everything
 *
 * @returns Always true.
 *
 * @public
 */
function dummyFilter() {
    return true;
}
/**
 * Builds a filter that filters mutations by given types.
 *
 * @param mutations - Mutation types that should trigger persisting.
 *
 * @public
 */
function mutationFilter(mutations) {
    const values = mutations.filter((value) => typeof value === 'string');
    const regexps = mutations.filter((value) => value instanceof RegExp);
    return function ({ type }) {
        return (values.indexOf(type) >= 0 || regexps.some((re) => re.test(type)));
    };
}

function replace(loaded) {
    return loaded;
}
/**
 * Replace each property of Vuex state with loaded one.
 *
 * @param loaded - State just loaded from the storage.
 * @param previous - State present in Vuex (probably defaults).
 *
 * @returns State that will be put into Vuex.
 *
 * @public
 */
function shallowMerge(loaded, previous) {
    return {
        ...previous,
        ...loaded,
    };
}
/**
 * Deep merge the states using [[deepmerge]] library (defaults except that arrays are replaced).
 *
 * @param loaded - State just loaded from the storage.
 * @param previous - State present in Vuex (probably defaults).
 *
 * @returns State that will be put into Vuex.
 *
 * @remarks
 * See [[configurableDeepMerge]] for customizations.
 *
 * @public
 */
function deepMerge(loaded, previous) {
    return deepmerge(previous, loaded, {
        // Replace arrays instead of concatenating them
        arrayMerge: (_destinationArray, sourceArray) => sourceArray,
    });
}
/**
 * Builds merger that deep merges the states using [[deepmerge]] library with custom options.
 *
 * @param options - Options to be passed to the library.
 *
 * @returns Merger that can be passed to [[LTM]].
 *
 * @public
 */
function configurableDeepMerge(options) {
    return function innerDeepMerge(loaded, previous) {
        return deepmerge(previous, loaded, options);
    };
}

/**
 * Saves the entire state.
 *
 * @public
 */
function saveAll(state) {
    return state;
}
/**
 * Saves only select modules.
 *
 * @param moduleNames - Names (as they appear in state object) of modules to be persisted.
 *
 * @public
 */
function pickModules(moduleNames) {
    return function (state) {
        const reduced = {};
        moduleNames.forEach((name) => {
            if (Object.prototype.hasOwnProperty.call(state, name)) {
                reduced[name] = state[name];
            }
        });
        return reduced;
    };
}

/**
 * Builds a custom storage that can be passed to [[LTM]].
 *
 * @typeparam Outer - The Vuex state type.
 * @typeparam Inner - The storage state type.
 */
class GenericStorageWrapper {
    key;
    setItem;
    getItem;
    toInner;
    toOuter;
    constructor(key, setItem, getItem, toInner, toOuter) {
        this.key = key;
        this.setItem = setItem;
        this.getItem = getItem;
        this.toInner = toInner;
        this.toOuter = toOuter;
    }
    async save(outer) {
        const inner = this.toInner(outer);
        this.setItem(this.key, inner);
    }
    async load() {
        const inner = await this.getItem(this.key);
        if (inner) {
            return this.toOuter(inner);
        }
        else {
            return null;
        }
    }
}

/**
 * Builds a storage wrapper for storages using the same API as chrome.storage.*.
 *
 * @param key - Key used to store the persisted state.
 * @param storage - The storage object (implementing chrome.storage.* API).
 * @param toInner - Custom conversion from state object to storage object (default is pass through).
 * @param toOuter - Custom conversion from storage object to state object (default is pass through).
 *
 * @typeparam Outer - The Vuex state type.
 * @typeparam Inner - The storage state type.
 *
 * @public
 */
function chromeStorageWrapper(key, storage, toInner = (data) => data, toOuter = (data) => data) {
    return new GenericStorageWrapper(key, function setItem(key, data) {
        return new Promise((resolve) => {
            storage.set({ [key]: data }, resolve);
        });
    }, function getItem(key) {
        return new Promise((resolve) => {
            storage.get([key], (result) => {
                resolve(result[key]);
            });
        });
    }, toInner, toOuter);
}

/* global chrome: false */
/**
 * Builds chrome.storage.local wrapper.
 *
 * @param key - Key used to store the persisted state.
 * @param toInner - Custom conversion from state object to storage object (default is pass through).
 * @param toOuter - Custom conversion from storage object to state object (default is pass through).
 *
 * @typeparam Outer - The Vuex state type.
 * @typeparam Inner - The storage state type.
 *
 * @public
 */
function chromeLocalStorage(key, toInner, toOuter) {
    return chromeStorageWrapper(key, chrome.storage.local, toInner, toOuter);
}

/* global chrome: false */
/**
 * Builds chrome.storage.sync wrapper.
 *
 * @param key - Key used to store the persisted state.
 * @param toInner - Custom conversion from state object to storage object (default is pass through).
 * @param toOuter - Custom conversion from storage object to state object (default is pass through).
 *
 * @typeparam Outer - The Vuex state type.
 * @typeparam Inner - The storage state type.
 *
 * @public
 */
function chromeSyncStorage(key, toInner, toOuter) {
    return chromeStorageWrapper(key, chrome.storage.sync, toInner, toOuter);
}

/**
 * Builds storage that keeps the state only in memory (the same way Vuex does).
 *
 * @remarks
 * Useful mainly for testing and development as it doesn't persist anything.
 *
 * @typeparam Outer - The Vuex state type.
 *
 * @public
 */
function inMemoryStorage(key) {
    const map = new Map();
    return new GenericStorageWrapper(key, function setItem(key, data) {
        map.set(key, data);
    }, function getItem(key) {
        const v = map.get(key);
        return v === undefined ? null : v;
    }, function toInner(outer) {
        return outer;
    }, function toOuter(inner) {
        return inner;
    });
}

/**
 * Builds a storage wrapper for storages using the same API as LocalForage.
 *
 * @param key - Key used to store the persisted state.
 * @param storage - The storage object (implementing LocalForage API).
 * @param toInner - Custom conversion from state object to storage object (default is pass through).
 * @param toOuter - Custom conversion from storage object to state object (default is pass through).
 *
 * @typeparam Outer - The Vuex state type.
 * @typeparam Inner - The storage state type.
 *
 * @public
 */
function localForageWrapper(key, storage, toInner = (data) => data, toOuter = (data) => data) {
    return new GenericStorageWrapper(key, function setItem(key, data) {
        return storage.setItem(key, data);
    }, function getItem(key) {
        return storage.getItem(key);
    }, toInner, toOuter);
}

const localForageDrivers = {
    INDEXEDDB: lf.INDEXEDDB,
    LOCALSTORAGE: lf.LOCALSTORAGE,
    WEBSQL: lf.WEBSQL,
};
/**
 * Builds LocalForage wrapper.
 *
 * @param key - Key used to store the persisted state.
 * @param options - Options to be passed to LocalForage.
 * @param toInner - Custom conversion from state object to storage object (default is pass through).
 * @param toOuter - Custom conversion from storage object to state object (default is pass through).
 *
 * @typeparam Outer - The Vuex state type.
 * @typeparam Inner - The storage state type.
 *
 * @public
 */
function localForage(key, options = {}, toInner, toOuter) {
    return localForageWrapper(key, lf.createInstance(options), toInner, toOuter);
}

/**
 * Builds a storage wrapper for storages using the same API as localStorage.
 *
 * @param key - Key used to store the persisted state.
 * @param storage - The storage object (implementing localStorage API).
 * @param toInner - Custom conversion from state object to string (default is JSON.stringify).
 * @param toOuter - Custom conversion from string to state object (default is JSON.parse).
 *
 * @typeparam Outer - The Vuex state type.
 *
 * @public
 */
function localStorageWrapper(key, storage, toInner = JSON.stringify.bind(JSON), toOuter = JSON.parse.bind(JSON)) {
    return new GenericStorageWrapper(key, function setItem(key, data) {
        storage.setItem(key, data);
    }, function getItem(key) {
        return storage.getItem(key);
    }, toInner, toOuter);
}

/**
 * Builds localStorage wrapper.
 *
 * @param key - Key used to store the persisted state.
 * @param toInner - Custom conversion from state object to string (default is JSON.stringify).
 * @param toOuter - Custom conversion from string to state object (default is JSON.parse).
 *
 * @typeparam Outer - The Vuex state type.
 *
 * @public
 */
function localStorage(key, toInner, toOuter) {
    return localStorageWrapper(key, window.localStorage, toInner, toOuter);
}

/**
 * Builds sessionStorage wrapper.
 *
 * @param key - Key used to store the persisted state.
 * @param toInner - Custom conversion from state object to string (default is JSON.stringify).
 * @param toOuter - Custom conversion from string to state object (default is JSON.parse).
 *
 * @typeparam Outer - The Vuex state type.
 * @typeparam Inner - The storage state type.
 *
 * @public
 */
function sessionStorage(key, toInner, toOuter) {
    return localStorageWrapper(key, window.sessionStorage, toInner, toOuter);
}

export { GenericStorageWrapper, LTM, chromeLocalStorage, chromeStorageWrapper, chromeSyncStorage, configurableDeepMerge, deepMerge, dummyFilter, executeWithDelay, inMemoryStorage, localForage, localForageDrivers, localForageWrapper, localStorage, localStorageWrapper, mutationFilter, pickModules, replace, saveAll, sessionStorage, shallowMerge, simplyExecute };
